import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../utils/api';
import { AudioPlayer } from '../components/AudioPlayer';
import { Instagram, Facebook, Youtube, Twitter, Music2, Linkedin } from 'lucide-react';

export default function HomePage() {
  const [artists, setArtists] = useState([]);

  // Hyper-realistic full body soul singer images
  const singerImages = [
    {
      id: 1,
      url: 'https://static.prod-images.emergentagent.com/jobs/0a80720e-f237-4d6e-94af-756f337617bc/images/d6815183c28497825b05c426eb874b0bea994db18a56c86d3358adcbcb805087.png',
      name: 'Marcus Soul',
      style: 'Classic Soul Vocalist'
    },
    {
      id: 2,
      url: 'https://customer-assets.emergentagent.com/job_indie-label-hub-2/artifacts/ejiyxeq0_DB699FEB-D749-4C29-B04E-C1EABCC96C18.png',
      name: 'Jasmine Harmony',
      style: 'Vintage Soul Songstress'
    },
    {
      id: 3,
      url: 'https://static.prod-images.emergentagent.com/jobs/0a80720e-f237-4d6e-94af-756f337617bc/images/3b2960e33bdb3a9ff1e7b614779a53b6cf5e829d533dfbd58ce8f7c90feba694.png',
      name: 'Andre Velvet',
      style: 'Modern Soul Artist'
    }
  ];

  useEffect(() => {
    fetchArtists();
  }, []);

  const fetchArtists = async () => {
    try {
      const response = await axios.get(`${API}/roster/artists`);
      setArtists(response.data.slice(0, 3));
    } catch (error) {
      
    }
  };

  return (
    <div className="min-h-screen" data-testid="homepage">
      {/* The Future of Vintage Soul Image - MOVED TO POSITION 1 */}
      <section className="pt-20 pb-6 bg-[#1A1412]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            data-testid="future-of-vintage-soul-image"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_indie-label-hub-2/artifacts/mwnopg0x_IMG_8052.jpeg"
              alt="The Future of Vintage Soul"
              className="w-full h-auto rounded-3xl shadow-[0_30px_80px_-20px_rgba(217,119,54,0.5)]"
            />
          </motion.div>
        </div>
      </section>

      {/* Voice of Vintage Soul Text Image */}
      <section className="py-6 bg-[#1A1412]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            data-testid="voice-of-vintage-soul-image"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_indie-label-hub-2/artifacts/o5hm8f1v_IMG_8217.jpeg"
              alt="The Voice of Vintage Soul"
              className="w-full h-auto rounded-2xl"
            />
          </motion.div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative flex items-center overflow-hidden py-16 min-h-[50vh]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1412] via-[#2C221F] to-[#1A1412]"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
          <div className="max-w-4xl mx-auto text-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="mb-4">
                <span className="text-[#CFB53B] uppercase tracking-[0.2em] text-xs font-['Outfit'] font-light">Vintage Soul Sounds</span>
              </div>
              <h1 className="font-['Playfair_Display'] text-5xl sm:text-6xl lg:text-7xl font-bold text-[#FDFBF7] leading-tight tracking-tight mb-6">
                Breaking the <br />
                <span className="italic text-[#D97736]">Frequency</span>
              </h1>
              <p className="text-[#A89B95] text-lg sm:text-xl mb-8 max-w-3xl mx-auto font-['Outfit']">
                Real music. Real artists. Auditions and casting for new vintage soul sounds with intimate vibrations.
              </p>
              <div className="flex flex-wrap gap-4 justify-center">
                <Link to="/music" data-testid="cta-submit-button">
                  <Button className="bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full px-8 py-6 text-lg shadow-[0_20px_40px_-15px_rgba(217,119,54,0.25)]">
                    Artist & Musicians
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Authenticity & Values Section */}
      <section className="py-8 bg-gradient-to-br from-[#2C221F] via-[#1A1412] to-[#2C221F] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #D97736 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-[0_30px_80px_-20px_rgba(217,119,54,0.4)]">
                <img
                  src="https://customer-assets.emergentagent.com/job_indie-label-hub-2/artifacts/2qxuooxp_1EA364D4-45CC-4275-AB91-8A46399E2A9A.png"
                  alt="Authentic Soul Artist"
                  className="w-full h-auto"
                />
              </div>
            </motion.div>

            {/* Right: Text Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="text-[#CFB53B] uppercase tracking-[0.2em] text-xs font-['Outfit'] font-light">Our Mission</span>
              <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#FDFBF7] mt-4 mb-6 tracking-tight">
                A Safe Haven for <span className="italic text-[#D97736]">Real Music</span>
              </h2>
              <div className="space-y-4 text-[#A89B95] text-lg font-['Outfit'] leading-relaxed">
                <p>
                  In a world of digital noise, we champion the <span className="text-[#FDFBF7] font-semibold">tangible</span> — music you can feel in your bones, hear in your heart, and hold in your hands.
                </p>
                <p>
                  We bring value to the <span className="text-[#FDFBF7] font-semibold">original and authentic</span> sound of soul. Every note matters. Every artist's voice deserves to be heard. Every vinyl pressed with care.
                </p>
                <p className="text-[#CFB53B] font-semibold italic">
                  This is more than music. This is preservation. This is legacy.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/shop">
                  <Button className="bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full px-8 py-6 text-lg animate-pulse">
                    Vintage Ave Merch
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Audio Player */}
      <section className="py-8 bg-[#1A1412]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <span className="text-[#CFB53B] uppercase tracking-[0.2em] text-xs font-['Outfit'] font-light">Listen Now</span>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#FDFBF7] mt-4 tracking-tight">
              Experience the <span className="italic text-[#D97736]">Sound</span>
            </h2>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <AudioPlayer 
              audioUrl="https://customer-assets.emergentagent.com/job_indie-label-hub-2/artifacts/ay8fv7d5_tony%E2%80%99s-luck.mp3"
              title="Tony's Luck"
              artist="Vintage Ave Records"
            />
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mt-8"
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_indie-label-hub-2/artifacts/7vc0aelm_IMG_8561.jpeg"
              alt="All roads to soul music lead through Vintage Ave"
              className="max-w-full md:max-w-3xl mx-auto rounded-2xl shadow-2xl"
            />
          </motion.p>
        </div>
      </section>

      {/* Featured Artist Showcase - Text Only */}
      <section className="py-8 bg-gradient-to-br from-[#2C221F] via-[#1A1412] to-[#2C221F] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #D97736 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
            data-testid="featured-artist-text"
          >
            <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#FDFBF7] mb-6 tracking-tight">
              The Voice of <span className="italic text-[#D97736]">Vintage Soul</span>
            </h2>
            <p className="text-[#FDFBF7] text-lg sm:text-xl mb-8 font-['Outfit'] leading-relaxed">
              Discover the authentic sound that defines our label. Raw emotion, timeless style, and intimate vibrations.
            </p>
            
            {/* Connect With Us - Compact with Individual Orange Gradient Icons */}
            <div className="inline-block">
              <h3 className="text-[#CFB53B] text-sm font-['Outfit'] uppercase tracking-widest mb-3">Connect With Us</h3>
              <div className="flex justify-center gap-3">
                <a href="https://instagram.com/vintageaverecords" target="_blank" rel="noopener noreferrer" className="group">
                  <div className="bg-gradient-to-br from-[#D97736] to-[#F08F4D] p-2.5 rounded-lg shadow-lg group-hover:shadow-[0_0_20px_rgba(217,119,54,0.6)] transition-all group-hover:scale-110">
                    <Instagram size={18} className="text-white" />
                  </div>
                </a>
                <a href="https://facebook.com/VINTAGEAVERECORDS" target="_blank" rel="noopener noreferrer" className="group">
                  <div className="bg-gradient-to-br from-[#D97736] to-[#F08F4D] p-2.5 rounded-lg shadow-lg group-hover:shadow-[0_0_20px_rgba(217,119,54,0.6)] transition-all group-hover:scale-110">
                    <Facebook size={18} className="text-white" />
                  </div>
                </a>
                <a href="https://youtube.com/@VintageAveRecordsSoulMusic" target="_blank" rel="noopener noreferrer" className="group">
                  <div className="bg-gradient-to-br from-[#D97736] to-[#F08F4D] p-2.5 rounded-lg shadow-lg group-hover:shadow-[0_0_20px_rgba(217,119,54,0.6)] transition-all group-hover:scale-110">
                    <Youtube size={18} className="text-white" />
                  </div>
                </a>
                <a href="https://twitter.com/VINTAGEAVEMUSIC" target="_blank" rel="noopener noreferrer" className="group">
                  <div className="bg-gradient-to-br from-[#D97736] to-[#F08F4D] p-2.5 rounded-lg shadow-lg group-hover:shadow-[0_0_20px_rgba(217,119,54,0.6)] transition-all group-hover:scale-110">
                    <Twitter size={18} className="text-white" />
                  </div>
                </a>
                <a href="https://linkedin.com/in/vintageaverecords" target="_blank" rel="noopener noreferrer" className="group">
                  <div className="bg-gradient-to-br from-[#D97736] to-[#F08F4D] p-2.5 rounded-lg shadow-lg group-hover:shadow-[0_0_20px_rgba(217,119,54,0.6)] transition-all group-hover:scale-110">
                    <Linkedin size={18} className="text-white" />
                  </div>
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Full Body Soul Singers Section - AI Generated */}
      <section className="py-8 bg-[#2C221F] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, #D97736 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-[#CFB53B] uppercase tracking-[0.2em] text-xs font-['Outfit'] font-light">The Soul of Vintage Ave</span>
            <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#FDFBF7] mt-4 tracking-tight">
              Our Artists, Your <span className="italic text-[#D97736]">Soundtrack</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {singerImages.map((singerImg, index) => (
              <motion.div
                key={singerImg.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="group relative overflow-hidden rounded-2xl shadow-[0_20px_60px_-15px_rgba(217,119,54,0.3)]"
                data-testid={`singer-image-${index}`}
              >
                <div className="aspect-[3/5] overflow-hidden bg-[#1A1412]">
                  <img
                    src={singerImg.url}
                    alt="Vintage Ave Artist"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mt-16"
          >
            <Link to="/music" data-testid="view-full-collection-link">
              <motion.span 
                className="text-8xl cursor-pointer inline-block hover:scale-110 transition-transform duration-300"
                animate={{ 
                  opacity: [1, 0.6, 1],
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  duration: 1.5, 
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                data-testid="music-emoji-button"
              >
                🎶
              </motion.span>
            </Link>
          </motion.div>
        </div>
      </section>


      {/* Auditions Banner */}
      <section className="py-8 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_0a80720e-f237-4d6e-94af-756f337617bc/artifacts/xvcl5wfr_IMG_7979.jpeg"
            alt="Auditions"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1412] via-[#1A1412]/90 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-testid="auditions-section">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#FDFBF7] mb-6 tracking-tight">
              Ready to Join Us?
            </h2>
            <p className="text-[#A89B95] text-lg sm:text-xl mb-8 max-w-2xl mx-auto font-['Outfit']">
              We're looking for talented artists and models who embody the vintage soul sound. Submit your work today.
            </p>
            <Link to="/submit" data-testid="join-cta-button">
              <Button className="bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full px-10 py-6 text-lg shadow-[0_20px_40px_-15px_rgba(217,119,54,0.25)]">
                Start Your Application
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}